<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>Subject Titles</h1>
        <Button label="Add Subject Title" icon="pi pi-plus" @click="openAdd" />
      </div>

      <DataTable :value="items" :paginator="true" :rows="10" :loading="loading" stripedRows showGridlines v-model:filters="filters" filterDisplay="menu">
        <template #header>
          <div class="table-header">
            <IconField iconPosition="left">
              <InputIcon class="pi pi-search" />
              <InputText v-model="filters['global'].value" placeholder="Search..." />
            </IconField>
          </div>
        </template>
        <Column field="name" header="Subject Title" sortable />
        <Column header="Actions" style="width: 10rem">
          <template #body="{ data }">
            <div class="actions">
              <Button icon="pi pi-pencil" severity="info" size="small" @click="openEdit(data)" />
              <Button icon="pi pi-trash" severity="danger" size="small" @click="deleteItem(data.id)" />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <Dialog v-model:visible="showModal" :header="isEditing ? 'Edit Subject Title' : 'Add Subject Title'" :modal="true" :style="{width: '800px'}">
      <div class="form-group" style="padding: 1rem 0;">
        <FloatLabel variant="in">
          <InputText id="name" v-model="form.name" class="w-full" />
          <label for="name">Subject Title *</label>
        </FloatLabel>
      </div>

      <!-- Legislative Status Table -->
      <div v-if="isEditing" class="section" style="margin-bottom: 1rem;">
        <div class="section-header">
          <h3 style="font-size: 0.95rem;">Legislative Status</h3>
          <Button icon="pi pi-plus" label="Add" size="small" @click="form.legislative_statuses.push({ status: '', date: null })" />
        </div>
        <DataTable :value="form.legislative_statuses" showGridlines size="small" v-if="form.legislative_statuses && form.legislative_statuses.length">
          <Column field="status" header="Status">
            <template #body="{ data }">
              <Select v-model="data.status" :options="legislativeStatusOptions" optionLabel="label" optionValue="value" class="w-full" />
            </template>
          </Column>
          <Column field="date" header="Date">
            <template #body="{ data }">
              <DatePicker v-model="data.date" dateFormat="yy-mm-dd" class="w-full" />
            </template>
          </Column>
          <Column header="" style="width: 4rem">
            <template #body="{ data, index }">
              <Button icon="pi pi-trash" severity="danger" size="small" text @click="form.legislative_statuses.splice(index, 1)" />
            </template>
          </Column>
        </DataTable>
      </div>

      <div class="modal-actions">
        <Button label="Cancel" severity="secondary" icon="pi pi-times" @click="showModal = false" />
        <Button :label="isEditing ? 'Update' : 'Create'" icon="pi pi-check" :loading="saving" @click="save" />
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'
import api from '../axios.js'

const items = ref([])
const loading = ref(false)
const showModal = ref(false)
const isEditing = ref(false)
const saving = ref(false)
const form = ref({ id: null, name: '', legislative_statuses: [] })
const filters = ref({ global: { value: null, matchMode: 'contains' } })

const legislativeStatusOptions = [
  { label: 'Pending at the Committee Level', value: 'Pending at the Committee Level' },
  { label: 'Approved on 2nd Reading', value: 'Approved on 2nd Reading' },
  { label: 'Approved on 3rd Reading', value: 'Approved on 3rd Reading' },
  { label: 'Transmitted to the Other Chamber', value: 'Transmitted to the Other Chamber' },
  { label: 'Consolidated', value: 'Consolidated' },
  { label: 'Bicameral Conference', value: 'Bicameral Conference' },
  { label: 'Ratified', value: 'Ratified' },
  { label: 'Enrolled', value: 'Enrolled' },
  { label: 'Signed into Law', value: 'Signed into Law' },
  { label: 'Vetoed', value: 'Vetoed' }
]

const load = async () => {
  loading.value = true
  try {
    const res = await api.get('/subject-titles')
    items.value = res.data
  } catch (e) { console.error(e) }
  finally { loading.value = false }
}

const openAdd = () => {
  isEditing.value = false
  form.value = { id: null, name: '', legislative_statuses: [] }
  showModal.value = true
}

const openEdit = (item) => {
  isEditing.value = true
  form.value = {
    ...item,
    legislative_statuses: (item.statuses || []).map(s => ({
      id: s.id,
      status: s.status,
      date: s.date
    }))
  }
  showModal.value = true
}

const save = async () => {
  if (!form.value.name) return
  saving.value = true
  try {
    const payload = {
      name: form.value.name,
      legislative_statuses: form.value.legislative_statuses || []
    }
    if (isEditing.value) {
      await api.put(`/subject-titles/${form.value.id}`, payload)
    } else {
      await api.post('/subject-titles', payload)
    }
    showModal.value = false
    load()
  } catch (e) {
    Swal.fire({ title: 'Error!', text: 'Failed to save', icon: 'error' })
  } finally { saving.value = false }
}

const deleteItem = async (id) => {
  const result = await Swal.fire({ title: 'Delete?', text: 'Are you sure?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#ef4444' })
  if (result.isConfirmed) {
    try {
      await api.delete(`/subject-titles/${id}`)
      load()
      Swal.fire({ title: 'Deleted!', text: 'Subject title deleted.', icon: 'success', timer: 1500, showConfirmButton: false })
    } catch (e) {
      const msg = e.response?.data?.message || 'Failed to delete'
      Swal.fire({ title: 'Cannot Delete', text: msg, icon: 'error' })
    }
  }
}

onMounted(load)
</script>

<style scoped>
.page { padding: 1rem 0; background: var(--p-surface-50); min-height: 100vh; }
.container { margin: 1rem; padding: 1.5rem; }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 2px solid #1e3a8a; }
.page-header h1 { color: #1e3a8a; font-size: 2rem; font-weight: 700; }
.table-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
.actions { display: flex; gap: 0.5rem; }
.form-group .p-inputtext { width: 100% !important; }
.modal-actions { display: flex; justify-content: flex-end; gap: 0.75rem; padding-top: 1rem; border-top: 1px solid #e5e7eb; }
:deep(.p-datatable .p-select),
:deep(.p-datatable .p-datepicker) { width: 100% !important; }
</style>
